import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import Layout from '@/components/Layout';
import { ArrowLeft, MessageSquare, User, Building2, Calendar, Tag, Share2, Copy, Check, CheckCircle, Edit, XCircle, MapPin, Heart, Star, Users, TrendingUp, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Share Icons - Compact
const WhatsAppIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const MailIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const TelegramIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const ListingDetail = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closeReason, setCloseReason] = useState('found_professional');
  const [closeNotes, setCloseNotes] = useState('');
  const [closing, setClosing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [matchingProfessionals, setMatchingProfessionals] = useState([]);
  const [loadingMatching, setLoadingMatching] = useState(false);
  const [showFeatureDialog, setShowFeatureDialog] = useState(false);
  const [featureDays, setFeatureDays] = useState(7);
  const [featuring, setFeaturing] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [id]);

  useEffect(() => {
    if (user?.user_type === 'professional' && id) {
      checkFavorite();
    }
  }, [user, id]);

  // Fetch matching professionals when listing is loaded and user is company
  useEffect(() => {
    if (listing && user?.user_type === 'company' && listing.listing_type === 'request') {
      fetchMatchingProfessionals();
    }
  }, [listing, user]);

  const fetchMatchingProfessionals = async () => {
    setLoadingMatching(true);
    try {
      const res = await axios.get(`${API}/listings/${id}/matching-professionals?limit=5`);
      setMatchingProfessionals(res.data);
    } catch (error) {
      console.error('Error fetching matching professionals:', error);
    } finally {
      setLoadingMatching(false);
    }
  };

  const checkFavorite = async () => {
    try {
      const res = await axios.get(`${API}/favorites/check/${id}`);
      setIsFavorite(res.data.is_favorite);
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  };

  const toggleFavorite = async () => {
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${id}`);
        setIsFavorite(false);
        toast.success('Rimosso dai preferiti');
      } else {
        await axios.post(`${API}/favorites`, { listing_id: id });
        setIsFavorite(true);
        toast.success('Aggiunto ai preferiti');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}`);
      setListing(response.data);
    } catch (error) {
      console.error('Error fetching listing:', error);
      toast.error('Errore nel caricamento dell\'annuncio');
      navigate('/listings');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseListing = async () => {
    setClosing(true);
    try {
      await axios.post(`${API}/listings/${id}/close`, {
        reason: closeReason,
        notes: closeNotes
      });
      toast.success('Annuncio chiuso con successo!');
      setShowCloseDialog(false);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error closing listing:', error);
      toast.error('Errore nella chiusura dell\'annuncio');
    } finally {
      setClosing(false);
    }
  };

  const handleFeatureListing = async () => {
    setFeaturing(true);
    try {
      await axios.post(`${API}/listings/${id}/feature?duration_days=${featureDays}`);
      toast.success(`Annuncio messo in evidenza per ${featureDays} giorni!`);
      setShowFeatureDialog(false);
      fetchListing(); // Refresh listing data
    } catch (error) {
      console.error('Error featuring listing:', error);
      toast.error(error.response?.data?.detail || 'Errore nel mettere in evidenza l\'annuncio');
    } finally {
      setFeaturing(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error('Scrivi un messaggio');
      return;
    }

    setSendingMessage(true);
    try {
      await axios.post(`${API}/messages`, {
        receiver_id: listing.user_id,
        content: message
      });
      toast.success('Messaggio inviato con successo!');
      setMessage('');
      setShowMessageDialog(false);
      // Navigate to messages
      const convId = [user.id, listing.user_id].sort().join('_');
      navigate(`/messages/${convId}`);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Errore nell\'invio del messaggio');
    } finally {
      setSendingMessage(false);
    }
  };

  // Share functions
  const getShareUrl = () => {
    return `${window.location.origin}/listings/${id}`;
  };

  const getShareText = () => {
    if (!listing) return '';
    return `${listing.title} - ${listing.listing_type === 'offer' ? 'Offerta' : 'Richiesta'} di servizio su ConsultantHUB`;
  };

  const handleCopyLink = async () => {
    const url = getShareUrl();
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers or non-HTTPS contexts
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      toast.success('Link copiato negli appunti!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Errore nel copiare il link');
    }
  };

  const handleShareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(getShareText() + '\n\n' + getShareUrl())}`;
    window.open(url, '_blank');
    setShowShareDialog(false);
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(getShareText());
    const body = encodeURIComponent(`Ho trovato questo annuncio interessante su ConsultantHUB:\n\n${listing.title}\n\nCategoria: ${listing.category}\nSettore: ${listing.sector}\n\nVedi l'annuncio completo: ${getShareUrl()}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShowShareDialog(false);
  };

  const handleShareTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(getShareUrl())}&text=${encodeURIComponent(getShareText())}`;
    window.open(url, '_blank');
    setShowShareDialog(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!listing) {
    return null;
  }

  const isOwner = listing.user_id === user.id;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-8 mt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-6 text-slate-600 hover:text-[#344B71]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>

          <Card className="p-8 bg-white">
            {/* Header */}
            <div className="flex items-start justify-between mb-6 pb-6 border-b border-slate-200">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-full flex items-center justify-center text-white">
                  {listing.user_type === 'professional' ? (
                    <User className="w-8 h-8" />
                  ) : (
                    <Building2 className="w-8 h-8" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#344B71]">{listing.user_name}</h2>
                  <p className="text-slate-600">
                    {listing.user_type === 'professional' ? 'Professionista' : 'Azienda'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* Featured Badge */}
                {listing.is_featured && (
                  <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-[#E8A007]/10 text-[#E8A007] flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4" />
                    In Evidenza
                  </span>
                )}
                
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  listing.listing_type === 'offer' 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-[#E8A007]/10 text-[#E8A007]'
                }`}>
                  {listing.listing_type === 'offer' ? 'Offerta di Servizio' : 'Richiesta di Servizio'}
                </span>
                
                {/* Priority badge - only for company listings */}
                {listing.user_type === 'company' && listing.priority && (
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                    listing.priority === 'alta' 
                      ? 'bg-red-100 text-red-700'
                      : listing.priority === 'media'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    Priorità {listing.priority.charAt(0).toUpperCase() + listing.priority.slice(1)}
                  </span>
                )}
                
                {/* Favorite Button - Only for professionals */}
                {user?.user_type === 'professional' && listing.user_id !== user.id && (
                  <Button
                    onClick={toggleFavorite}
                    variant="outline"
                    size="sm"
                    className={`border-slate-200 hover:bg-red-50 ${isFavorite ? 'bg-red-50 border-red-200 text-red-500' : ''}`}
                    data-testid="favorite-button"
                  >
                    <Heart className={`w-4 h-4 mr-2 ${isFavorite ? 'fill-red-500' : ''}`} />
                    {isFavorite ? 'Salvato' : 'Salva'}
                  </Button>
                )}
                
                {/* Share Button */}
                <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-slate-200 hover:bg-slate-50"
                      data-testid="share-button"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Condividi
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white sm:max-w-lg p-6 overflow-hidden">
                    <DialogHeader>
                      <DialogTitle className="text-lg font-semibold text-[#344B71]">Condividi annuncio</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-3 overflow-hidden">
                      <p className="text-sm text-slate-600">
                        Condividi con i tuoi contatti. Visibile solo agli utenti registrati.
                      </p>
                      
                      {/* Share Options - Compact horizontal layout */}
                      <div className="flex justify-center space-x-3">
                        <Button
                          onClick={handleShareWhatsApp}
                          size="sm"
                          className="bg-[#25D366] hover:bg-[#1da851] text-white px-4 py-2 h-10 rounded-md"
                          data-testid="share-whatsapp"
                        >
                          <WhatsAppIcon />
                          <span className="ml-2 text-sm">WhatsApp</span>
                        </Button>
                        <Button
                          onClick={handleShareEmail}
                          size="sm"
                          className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 h-10 rounded-md"
                          data-testid="share-email"
                        >
                          <MailIcon />
                          <span className="ml-2 text-sm">Email</span>
                        </Button>
                        <Button
                          onClick={handleShareTelegram}
                          size="sm"
                          className="bg-[#0088cc] hover:bg-[#006699] text-white px-4 py-2 h-10 rounded-md"
                          data-testid="share-telegram"
                        >
                          <TelegramIcon />
                          <span className="ml-2 text-sm">Telegram</span>
                        </Button>
                      </div>
                      
                      {/* Copy Link - Compact */}
                      <div className="pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-500 mb-2">Oppure copia il link:</p>
                        <div className="flex items-center gap-2 w-full max-w-full overflow-hidden">
                          <div className="flex-1 min-w-0 p-3 bg-slate-100 rounded-lg overflow-hidden">
                            <p className="text-xs text-slate-600 font-mono break-all line-clamp-1">
                              {getShareUrl()}
                            </p>
                          </div>
                          <Button
                            onClick={handleCopyLink}
                            size="sm"
                            className="flex-shrink-0 bg-[#344B71] hover:bg-[#344B71] text-white h-10 px-4 rounded-lg whitespace-nowrap"
                            data-testid="copy-link-button"
                          >
                            {copied ? (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                <span className="text-xs">Copiato!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4 mr-1" />
                                <span className="text-xs">Copia</span>
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl font-playfair font-bold text-[#344B71] mb-6" data-testid="listing-title">
              {listing.title}
            </h1>

            {/* Meta Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                <Tag className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="text-xs text-slate-500">Categoria</p>
                  <p className="text-sm font-semibold text-[#344B71]">{listing.category}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                <Building2 className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="text-xs text-slate-500">Settore</p>
                  <p className="text-sm font-semibold text-[#344B71]">{listing.sector}</p>
                </div>
              </div>
              {/* Location - only for company listings */}
              {listing.user_type === 'company' && listing.location_city && (
                <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-blue-600">Luogo Prestazione</p>
                    <p className="text-sm font-semibold text-[#344B71]">
                      {listing.location_city}{listing.location_province ? `, ${listing.location_province}` : ''}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                <Calendar className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="text-xs text-slate-500">Pubblicato il</p>
                  <p className="text-sm font-semibold text-[#344B71]">
                    {new Date(listing.created_at).toLocaleDateString('it-IT', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h3 className="text-xl font-playfair font-semibold text-[#344B71] mb-4">Descrizione</h3>
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="listing-description">
                {listing.description}
              </p>
            </div>

            {/* Action Button */}
            {!isOwner && (
              <div className="pt-6 border-t border-slate-200">
                <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      className="w-full md:w-auto bg-[#344B71] hover:bg-[#344B71] text-white px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all"
                      data-testid="contact-button"
                    >
                      <MessageSquare className="w-5 h-5 mr-2" />
                      Contatta {listing.user_type === 'professional' ? 'il Professionista' : 'l\'Azienda'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white">
                    <DialogHeader>
                      <DialogTitle className="text-[#344B71]">Invia un messaggio a {listing.user_name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <Textarea
                        placeholder="Scrivi il tuo messaggio..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={6}
                        className="bg-white text-[#344B71] border-slate-300 focus:border-[#344B71] focus:ring-[#344B71]"
                        data-testid="message-textarea"
                      />
                      <Button
                        onClick={handleSendMessage}
                        disabled={sendingMessage}
                        className="w-full bg-[#344B71] hover:bg-[#344B71] text-white rounded-xl"
                        data-testid="send-message-button"
                      >
                        {sendingMessage ? 'Invio...' : 'Invia Messaggio'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            {isOwner && (
              <div className="pt-6 border-t border-slate-200">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0">
                  <div className="bg-slate-50 p-4 rounded-lg flex-1 md:mr-4">
                    <p className="text-sm text-slate-600">
                      Questo è il tuo annuncio. Gli altri utenti possono contattarti tramite messaggi.
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={() => navigate(`/listings/${listing.id}/edit`)}
                      className="bg-[#E8A007] hover:bg-[#E09612] text-white px-6 rounded-full"
                      data-testid="edit-listing-button"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Modifica
                    </Button>
                    
                    {/* Close Listing Dialog */}
                    <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="border-green-600 text-green-700 hover:bg-green-50 px-6"
                          data-testid="close-listing-button"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Chiudi Annuncio
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-white sm:max-w-lg">
                        <DialogHeader>
                          <DialogTitle className="text-xl font-semibold text-[#344B71]">Chiudi Annuncio</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-6 mt-4">
                          <p className="text-slate-600">
                            Stai per chiudere questo annuncio. Una volta chiuso, non sarà più visibile agli altri utenti.
                          </p>
                          
                          <div>
                            <label className="text-sm font-medium text-slate-700 mb-2 block">
                              Motivo della chiusura
                            </label>
                            <Select value={closeReason} onValueChange={setCloseReason}>
                              <SelectTrigger className="h-12">
                                <SelectValue placeholder="Seleziona motivo" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="found_professional">
                                  <span className="flex items-center">
                                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                                    Ho trovato il professionista
                                  </span>
                                </SelectItem>
                                <SelectItem value="cancelled">
                                  <span className="flex items-center">
                                    <XCircle className="w-4 h-4 mr-2 text-orange-600" />
                                    Annullato / Non più necessario
                                  </span>
                                </SelectItem>
                                <SelectItem value="other">
                                  <span className="flex items-center">
                                    Altro motivo
                                  </span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <label className="text-sm font-medium text-slate-700 mb-2 block">
                              Note (opzionale)
                            </label>
                            <Textarea
                              value={closeNotes}
                              onChange={(e) => setCloseNotes(e.target.value)}
                              placeholder="Aggiungi eventuali note..."
                              rows={3}
                              className="text-[#344B71] focus:border-[#344B71] focus:ring-[#344B71]"
                            />
                          </div>
                          
                          <div className="flex space-x-3">
                            <Button
                              onClick={() => setShowCloseDialog(false)}
                              variant="outline"
                              className="flex-1 border-slate-300 rounded-xl"
                            >
                              Annulla
                            </Button>
                            <Button
                              onClick={handleCloseListing}
                              disabled={closing}
                              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                            >
                              {closing ? 'Chiusura...' : 'Conferma Chiusura'}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    {/* Feature Listing Dialog - Only for companies */}
                    {user?.user_type === 'company' && (
                      <Dialog open={showFeatureDialog} onOpenChange={setShowFeatureDialog}>
                        <DialogTrigger asChild>
                          <Button
                            className={`px-6 ${listing.is_featured 
                              ? 'bg-[#E8A007]/20 text-[#E8A007] border border-[#E8A007] hover:bg-[#E8A007]/30' 
                              : 'bg-[#E8A007] hover:bg-[#E09612] text-white'
                            } rounded-full`}
                            data-testid="feature-listing-button"
                          >
                            <Sparkles className="w-4 h-4 mr-2" />
                            {listing.is_featured ? 'In Evidenza' : 'Metti in Evidenza'}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle className="text-xl font-semibold text-[#344B71] flex items-center gap-2">
                              <Sparkles className="w-5 h-5 text-[#E8A007]" />
                              Metti in Evidenza
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-6 mt-4">
                            {listing.is_featured ? (
                              <>
                                <div className="p-4 bg-[#E8A007]/10 rounded-xl border border-[#E8A007]/30">
                                  <p className="text-[#E8A007] font-medium mb-1">Questo annuncio è già in evidenza!</p>
                                  <p className="text-sm text-slate-600">
                                    Visibile in evidenza fino al: {new Date(listing.featured_until).toLocaleDateString('it-IT', {
                                      day: 'numeric',
                                      month: 'long',
                                      year: 'numeric'
                                    })}
                                  </p>
                                </div>
                                <p className="text-slate-600 text-sm">
                                  Puoi estendere il periodo di evidenza selezionando una nuova durata.
                                </p>
                              </>
                            ) : (
                              <p className="text-slate-600">
                                Gli annunci in evidenza appaiono in cima alle ricerche e ricevono più visualizzazioni.
                              </p>
                            )}
                            
                            <div>
                              <label className="text-sm font-medium text-slate-700 mb-2 block">
                                Durata evidenza
                              </label>
                              <Select value={featureDays.toString()} onValueChange={(v) => setFeatureDays(parseInt(v))}>
                                <SelectTrigger className="h-12">
                                  <SelectValue placeholder="Seleziona durata" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="7">7 giorni</SelectItem>
                                  <SelectItem value="14">14 giorni</SelectItem>
                                  <SelectItem value="30">30 giorni</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="p-3 bg-slate-50 rounded-lg">
                              <p className="text-xs text-slate-500">
                                ℹ️ Gli annunci in evidenza vengono mostrati nella sezione dedicata e ottengono maggiore visibilità.
                              </p>
                            </div>
                            
                            <div className="flex space-x-3">
                              <Button
                                onClick={() => setShowFeatureDialog(false)}
                                variant="outline"
                                className="flex-1 border-slate-300 rounded-xl"
                              >
                                Annulla
                              </Button>
                              <Button
                                onClick={handleFeatureListing}
                                disabled={featuring}
                                className="flex-1 bg-[#E8A007] hover:bg-[#E09612] text-white rounded-xl"
                              >
                                {featuring ? 'Attivazione...' : (listing.is_featured ? 'Estendi Periodo' : 'Attiva Evidenza')}
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Matching Professionals Section - Only for companies viewing their own listings */}
          {user?.user_type === 'company' && listing?.listing_type === 'request' && listing?.user_id === user?.id && (
            <Card className="mt-6 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#344B71]/10 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#344B71]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#344B71]">Professionisti Suggeriti</h3>
                  <p className="text-sm text-slate-500">Basati sulla categoria e settore del tuo annuncio</p>
                </div>
              </div>

              {loadingMatching ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="animate-pulse bg-slate-100 rounded-xl h-32" />
                  ))}
                </div>
              ) : matchingProfessionals.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nessun professionista trovato per questo annuncio</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {matchingProfessionals.map((match, index) => (
                    <div 
                      key={match.user.id}
                      className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer border border-transparent hover:border-[#344B71]/20"
                      onClick={() => navigate(`/professionals/${match.user.id}`)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-[#344B71]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-[#344B71]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-[#344B71] truncate">{match.user.name}</span>
                            {match.profile?.is_verified && (
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            )}
                            <span className="px-2 py-0.5 bg-[#E8A007]/20 text-[#E8A007] text-xs font-medium rounded-full flex-shrink-0">
                              {match.match_score}% match
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 truncate">{match.profile?.professional_category}</p>
                          <div className="flex items-center gap-3 mt-2">
                            {match.average_rating > 0 && (
                              <div className="flex items-center gap-1">
                                <Star className="w-3 h-3 fill-[#E8A007] text-[#E8A007]" />
                                <span className="text-xs text-slate-600">{match.average_rating}</span>
                              </div>
                            )}
                            {match.profile?.experience_years && (
                              <span className="text-xs text-slate-500">{match.profile.experience_years} anni exp.</span>
                            )}
                            {match.profile?.iscrizione_albo && (
                              <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Albo</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

export default ListingDetail;
